import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import JsonLd from '../components/JsonLd';
import {
  buildOrganizationSchema,
  buildWebSiteSchema,
  buildWebPageSchema,
  buildSchemaGraph,
  formatDescription,
} from '../utils/seo';
import Footer from '../Footer';
import './SeoLandingPage.css';
import './SeoToolPage.css';

const APP_DOWNLOAD_URL = 'https://merra.onelink.me/uXXR/0vws5k9w';
const APP_DOWNLOAD_QR_SRC = `${process.env.PUBLIC_URL}/appsflyer-merra-0vws5k9w_qr_code.png`;

export default function SeoToolPage({
  canonicalPath,
  title,
  description,
  keywords,
  h1,
  subheading,
  platformLabel = 'TikTok',
  handlePlaceholder = '@yourhandle',
  inputLabel = 'Your handle',
  ctaLabel = 'Check now',
  analyzingLabel = 'Analyzing…',
  resultTitle,
  resultParagraphs = [],
  resultBullets = [],
  sections = [],
  faqs = [],
  relatedLinks = [],
  relatedBlogLinks = [],
  primaryCtaLabel = 'Try Merra AI free',
}) {
  const canonicalUrl = `https://www.merra.ai${canonicalPath}`;
  const [handle, setHandle] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | done
  const [checkedHandle, setCheckedHandle] = useState('');
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 768 : false));
  const [isDownloadBannerDismissed, setIsDownloadBannerDismissed] = useState(() => {
    try {
      return localStorage.getItem('downloadBannerDismissed') === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const showMobileDownloadBanner = isMobile && !isDownloadBannerDismissed;

  const handleGoogleSignup = () => {
    const backendUrl =
      process.env.NODE_ENV === 'production'
        ? 'https://proto-backend-d2c1638486b9.herokuapp.com'
        : 'http://localhost:5000';
    window.location.href = `${backendUrl}/auth/google`;
  };

  const dismissDownloadBanner = () => {
    setIsDownloadBannerDismissed(true);
    try {
      localStorage.setItem('downloadBannerDismissed', 'true');
    } catch {}
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const cleaned = String(handle || '').trim().replace(/^@+/, '');
    if (!cleaned) return;
    setStatus('loading');
    setCheckedHandle(cleaned);
    window.setTimeout(() => setStatus('done'), 900);
  };

  const faqJsonLd =
    faqs && faqs.length
      ? {
          '@type': 'FAQPage',
          mainEntity: faqs.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }
      : null;

  const pageSchema = buildSchemaGraph([
    buildOrganizationSchema(),
    buildWebSiteSchema(),
    buildWebPageSchema({
      name: title,
      description: formatDescription(description),
      url: canonicalUrl,
    }),
    faqJsonLd,
  ]);

  const wrapperClassName = useMemo(
    () => `seo-landing seo-tool${showMobileDownloadBanner ? ' has-download-banner' : ''}`,
    [showMobileDownloadBanner]
  );

  const displayHandle = checkedHandle ? `@${checkedHandle}` : '';

  return (
    <div className={wrapperClassName}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        {keywords ? <meta name="keywords" content={keywords} /> : null}
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
      </Helmet>
      <JsonLd schema={pageSchema} />

      <div className="download-qr-float" aria-hidden={isMobile}>
        <a
          href={APP_DOWNLOAD_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="download-qr-float__card"
          aria-label="Download Merra AI app"
        >
          <img src={APP_DOWNLOAD_QR_SRC} alt="Scan to download Merra AI" className="download-qr-float__img" loading="lazy" />
          <div className="download-qr-float__text">
            <div className="download-qr-float__title">Get Merra AI</div>
            <div className="download-qr-float__subtitle">Scan to get the app</div>
          </div>
        </a>
      </div>

      {showMobileDownloadBanner && (
        <div className="download-banner" role="region" aria-label="Download Merra AI app">
          <button type="button" className="download-banner__close" onClick={dismissDownloadBanner} aria-label="Dismiss download banner">
            ×
          </button>
          <div className="download-banner__content">
            <div className="download-banner__title">Download Merra AI</div>
            <div className="download-banner__subtitle">AI video editor on iPhone</div>
          </div>
          <a className="download-banner__cta" href={APP_DOWNLOAD_URL} target="_blank" rel="noopener noreferrer">
            Download
          </a>
        </div>
      )}

      <div className="seo-landing__container">
        <div className="seo-landing__nav">
          <Link to="/landing-page" className="seo-landing__brand">
            Merra AI
          </Link>
          <div className="seo-landing__navlinks">
            <Link to="/blog" className="seo-landing__link" target="_blank" rel="noopener noreferrer">
              Blog
            </Link>
            <Link to="/about" className="seo-landing__link" target="_blank" rel="noopener noreferrer">
              About
            </Link>
          </div>
        </div>

        <div className="seo-tool__hero">
          <div className="seo-landing__heroCard seo-tool__heroCard">
            <div className="seo-tool__eyebrow">{platformLabel} free tool</div>
            <h1 className="seo-landing__h1">{h1}</h1>
            <p className="seo-landing__sub">{subheading}</p>

            <form className="seo-tool__form" onSubmit={onSubmit}>
              <label className="seo-tool__label" htmlFor="seo-tool-handle">
                {inputLabel}
              </label>
              <div className="seo-tool__row">
                <input
                  id="seo-tool-handle"
                  className="seo-tool__input"
                  type="text"
                  value={handle}
                  onChange={(e) => setHandle(e.target.value)}
                  placeholder={handlePlaceholder}
                  autoComplete="off"
                  spellCheck={false}
                />
                <button type="submit" className="seo-landing__ctaPrimary seo-tool__submit" disabled={status === 'loading'}>
                  {status === 'loading' ? analyzingLabel : ctaLabel}
                </button>
              </div>
            </form>

            {status === 'done' && (
              <div className="seo-tool__result" role="status" aria-live="polite">
                <div className="seo-tool__resultBadge">Result for {displayHandle}</div>
                <h2 className="seo-tool__resultTitle">{resultTitle}</h2>
                {resultParagraphs.map((p) => (
                  <p key={p} className="seo-tool__resultP">
                    {p}
                  </p>
                ))}
                {resultBullets?.length > 0 && (
                  <ul className="seo-landing__featureList">
                    {resultBullets.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                )}
                <div className="seo-landing__ctaRow" style={{ marginTop: 16 }}>
                  <button type="button" className="seo-landing__ctaPrimary" onClick={handleGoogleSignup}>
                    {primaryCtaLabel}
                  </button>
                </div>
                <div className="seo-landing__fineprint">First 3 videos are free. No credit card required.</div>
              </div>
            )}
          </div>
        </div>

        {sections?.map((s) => (
          <div key={s.title} className="seo-landing__section" style={{ marginTop: 18 }}>
            <div className="seo-landing__h2">{s.title}</div>
            <p className="seo-landing__p">{s.body}</p>
          </div>
        ))}

        {faqs?.length > 0 && (
          <div className="seo-landing__section" style={{ marginTop: 18 }}>
            <div className="seo-landing__h2">FAQ</div>
            <div className="seo-landing__faq">
              {faqs.map((f) => (
                <details key={f.q} className="seo-landing__faqItem">
                  <summary>{f.q}</summary>
                  <p>{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        )}

        {(relatedLinks?.length > 0 || relatedBlogLinks?.length > 0) && (
          <div className="seo-landing__section" style={{ marginTop: 18 }}>
            <div className="seo-landing__h2">Related</div>
            <div className="seo-landing__related">
              {relatedLinks.map((l) => (
                <Link key={l.to} to={l.to} className="seo-landing__relatedLink">
                  {l.label}
                </Link>
              ))}
              {relatedBlogLinks.map((l) => (
                <Link key={l.to} to={l.to} className="seo-landing__relatedLink" target="_blank" rel="noopener noreferrer">
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
